import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { AUTH_CONFIG } from '../../config';

/**
 * Protected Route Component
 *
 * This component handles route protection based on authentication status and role requirements.
 *
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Child components to render if access is granted
 * @param {string} props.requiredRole Optional role required to access this route
 * @param {string} props.requiredAccess Optional access group required for this route
 * @returns {React.ReactNode} The protected component or redirect
 */
function ProtectedRoute({ children, requiredRole, requiredAccess }) {
  // Access auth context safely with null handling
  const auth = useAuth();
  const currentUser = auth?.currentUser;
  const loading = auth?.loading || false;
  const navigate = useNavigate();
  const [accessDenied, setAccessDenied] = useState(false);

  // Safely check for the hasRole function
  const checkRole = (role) => {
    if (!auth) return false;
    if (typeof auth.hasRole !== 'function') {
      console.error('hasRole function not available in auth context');
      return false;
    }
    return auth.hasRole(role);
  };

  const checkAccess = (access) => {
    if (!auth) return false;
    if (access === 'financials') return auth.canViewFinancials?.() || false;
    if (access === 'inventory') return auth.canViewInventory?.() || false;
    if (access === 'sensitiveInventory') return auth.canViewSensitiveInventory?.() || false;
    return false;
  };

  useEffect(() => {
    if (accessDenied) {
      const timer = setTimeout(() => navigate('/', { replace: true }), 2500);
      return () => clearTimeout(timer);
    }
  }, [accessDenied, navigate]);

  // Show loading indicator while authentication state is being determined
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  // Check role requirements if specified
  if (requiredRole && currentUser) {
    // Get the actual role value from AUTH_CONFIG if it's a key
    const roleValue = AUTH_CONFIG.roles[requiredRole] || requiredRole;

    // Check if user has the required role OR is a SuperAdmin/SysAdmin (who has access to everything)
    const hasRequiredRole = checkRole(roleValue);
    const isSuperAdmin = checkRole('SuperAdmin') || checkRole('SysAdmin');

    if (!hasRequiredRole && !isSuperAdmin) {
      console.log(`User lacks required role: ${roleValue}`);
      if (!accessDenied) setAccessDenied(true);
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
          <div style={{ padding: '24px 32px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, maxWidth: 480, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
            <strong style={{ fontSize: 18 }}>Access Denied</strong>
            <p style={{ margin: '12px 0 0', color: '#555' }}>
              You don&apos;t have permission to view this page.<br />
              Contact a SuperAdmin if you need access.
            </p>
            <p style={{ margin: '8px 0 0', color: '#888', fontSize: 13 }}>Redirecting you home&hellip;</p>
          </div>
        </div>
      );
    }
  }

  if (requiredAccess && currentUser) {
    const isSuperAdmin = checkRole('SuperAdmin') || checkRole('SysAdmin');
    if (!checkAccess(requiredAccess) && !isSuperAdmin) {
      console.log(`User lacks required access: ${requiredAccess}`);
      if (!accessDenied) setAccessDenied(true);
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
          <div style={{ padding: '24px 32px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, maxWidth: 480, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
            <strong style={{ fontSize: 18 }}>Access Denied</strong>
            <p style={{ margin: '12px 0 0', color: '#555' }}>
              You don&apos;t have permission to view this page.<br />
              Contact a SuperAdmin if you need access.
            </p>
            <p style={{ margin: '8px 0 0', color: '#888', fontSize: 13 }}>Redirecting you home&hellip;</p>
          </div>
        </div>
      );
    }
  }

  // User is authenticated and has required role (if any), render children
  return children;
}

export default ProtectedRoute;
